import React, { Suspense, useRef, useState, useEffect, useCallback, type CSSProperties, type RefObject } from 'react';
import { Menu, X, ChevronRight, ArrowUpRight, Star, Check, Calendar, Camera, Building2, Smartphone, ShieldCheck, Users, Video, Sparkles, Waves, ImageIcon, Phone, Mail, ArrowRight } from 'lucide-react';
import { GoogleReviewBadge } from './components/GoogleReviewBadge';
import FloatingCTA from './components/FloatingCTA';
import { EventType } from './types';
import { googleTestimonials } from './data/testimonials';
import { useAutoHorizontalScroll } from './hooks/useAutoHorizontalScroll';
import { useBooking } from './context/BookingContext';

const ChakraBookingModal = React.lazy(() => import('./components/ChakraBookingModal'));

type VideoKey = 'heroMobile' | 'corporate' | 'wedding';

const PHONE_NUMBER = '0466640484';
const PHONE_NUMBER_DISPLAY = '0466 640 484';
const CALL_LINK = `tel:${PHONE_NUMBER}`;
const TEXT_LINK = `sms:${PHONE_NUMBER}`;

const carouselTrackClasses = 'flex flex-nowrap gap-6 overflow-x-auto pb-6 px-6 no-scrollbar snap-x snap-proximity';
const containerRadius = 'rounded-[36px]';
const baseCarouselCardClasses = `flex-none w-[280px] lg:w-[360px] aspect-[4/5] ${containerRadius} border border-white/70 bg-white shadow-xl shadow-black/5 snap-center`;
const imageCarouselCardClasses = `${baseCarouselCardClasses} overflow-hidden relative group`;
const contentCarouselCardClasses = `${baseCarouselCardClasses} flex flex-col p-6 lg:p-8`;
const featureMediaClasses =
  'relative overflow-hidden group w-full aspect-[4/5] md:aspect-[4/5] lg:min-h-[360px] lg:h-full lg:w-full';
const heroVideoStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  border: 'none',
};
const heroPosterStyle: CSSProperties = {
  ...heroVideoStyle,
  filter: 'blur(4px)',
  transform: 'scale(1.08)',
};
const heroOverlayStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.1) 60%, rgba(0,0,0,0) 100%)',
  pointerEvents: 'none',
  zIndex: 2,
};
const carouselCardCropStyle: CSSProperties = {
  clipPath: 'inset(0 0 5% 0)',
};
const sectionPadding = 'py-12 md:py-24 px-6';
const lightSectionClasses = `${sectionPadding} bg-white`;
const darkSectionClasses = `${sectionPadding} bg-white overflow-hidden relative`;
const sectionWrapper = 'w-full max-w-[1200px] mx-auto';
const fullBleedWrapper = `${sectionWrapper} max-w-none px-0 sm:px-6`;
const wideSectionWrapper = 'max-w-[1400px] mx-auto px-6';
const cardShellClasses = `bg-white ${containerRadius} border border-white/70 shadow-xl shadow-black/5 overflow-hidden`;
const primaryCtaClasses = 'bg-champagne hover:bg-amber-600 text-white px-10 py-3.5 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] transition-all shadow-xl shadow-champagne/20 active:scale-95';
const pillLabelClasses = 'text-[10px] font-bold uppercase tracking-[0.3em] text-gray-400';
const sectionTitleClasses = 'text-3xl md:text-5xl font-display font-medium tracking-tight text-apple-black';
const sectionSubtitleClasses = 'text-sm md:text-base text-apple-gray font-light leading-relaxed';
const sectionIntroClasses = 'space-y-2 md:space-y-3';
const accentBarClasses = 'w-12 h-1 rounded-full bg-champagne mx-auto md:mx-0';
const sectionCardClasses = `${cardShellClasses} p-6 sm:p-8 md:p-12`;
const sectionStackClasses = 'space-y-10 md:space-y-16';

const galleryImages = [
  '/images/optimized/BTC2_ultra.webp',
  '/images/optimized/IMG_1154_ultra.webp',
  '/images/optimized/IMG_1253_ultra.webp',
  '/images/optimized/IMG_8282 (1)_ultra.webp',
  '/images/optimized/IMG_8404_ultra.webp',
  '/images/optimized/btcpark.webp',
  '/images/optimized/FullSizeRender_ultra.webp',
  '/images/optimized/IMG_1173_ultra.webp',
  '/images/optimized/IMG_1259_ultra.webp',
  '/images/optimized/IMG_5014_ultra.webp',
] as const;

const trustedPartners = [
  { name: 'Brimbank City Council', logo: '/logo/brimbanklogo.jpg' },
  { name: 'Maribyrnong City Council', logo: '/logo/Maribyrnong_city_council_logo.svg.png' },
  { name: 'Wyndham City Council', logo: '/logo/Wyndham_City_logo.svg.png' },
  { name: 'AGL', logo: '/logo/AGL_Energy_logo.svg' },
  { name: 'ZED Real Estate', logo: '/logo/zed_realestate.png' },
  { name: 'Bulldogs Foundation', logo: '/logo/western-bulldogs-comunity-foundation-logo.jpg' },
  { name: 'NECA Education', logo: '/logo/Neca_logo.jpeg' },
] as const;

const premiumTestimonials = googleTestimonials;

const getTestimonialTime = (relativeTime?: string, created?: string) => {
  if (relativeTime) return relativeTime;
  if (created) {
    try {
      return new Intl.DateTimeFormat('en-AU', { month: 'short', year: 'numeric' }).format(new Date(created));
    } catch {
      return 'Recent';
    }
  }
  return 'Recent';
};

const getReviewerInitials = (name: string) => {
  if (!name) return '360';
  const parts = name.trim().split(/\s+/);
  const initials = parts.slice(0, 2).map((part) => part[0]?.toUpperCase() ?? '');
  return initials.join('') || '360';
};

const quickLinks = [
  { label: 'Corporate', href: '#corporate' },
  { label: 'Weddings', href: '#weddings' },
  { label: 'Experience', href: '#experience' },
  { label: 'Bundles', href: '#bundles' },
  { label: 'Gallery', href: '#gallery' },
];

function App() {
  const { 
    isModalOpen, 
    closeModal, 
    openModal, 
    quickDate, 
    setQuickDate, 
    quickType, 
    setQuickType,
    selectedEventType,
    selectedDate 
  } = useBooking();
  
  const [showFloatingCTA, setShowFloatingCTA] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [videoPlayback, setVideoPlayback] = useState<Record<VideoKey, boolean>>({
    heroMobile: true,
    corporate: true,
    wedding: true,
  });
  const [videoLoaded, setVideoLoaded] = useState<Record<VideoKey, boolean>>({
    heroMobile: false,
    corporate: false,
    wedding: false,
  });

  // Refs for scroll observation
  const heroRef = useRef<HTMLDivElement>(null);
  const corporateRef = useRef<HTMLDivElement>(null);
  const weddingRef = useRef<HTMLDivElement>(null);
  const galleryRef = useRef<HTMLDivElement>(null);
  const testimonialsRef = useRef<HTMLDivElement>(null);

  const handleVideoPlay = (key: VideoKey) => {
    setVideoPlayback((prev) => ({ ...prev, [key]: true }));
  };

  const handleVideoLoaded = useCallback((key: VideoKey, options?: { delayMs?: number }) => {
    const markLoaded = () => setVideoLoaded((prev) => ({ ...prev, [key]: true }));
    if (options?.delayMs) {
      setTimeout(markLoaded, options.delayMs);
    } else {
      markLoaded();
    }
  }, []);

  const handleQuickQuoteSubmit = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    openModal(undefined, quickDate || undefined);
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const heroSection = document.getElementById('hero');
      const hideSectionIds = ['contact'];
      const criticalSections = hideSectionIds
        .map(id => document.getElementById(id))
        .filter((section): section is HTMLElement => Boolean(section));
      const ctaBlocks = document.querySelectorAll<HTMLElement>('[data-cta-block="true"]');

      const heroStillDominant = heroSection
        ? heroSection.getBoundingClientRect().bottom > window.innerHeight * 0.4
        : scrollY < 180;

      const isOverCriticalSection = criticalSections.some(section => {
        const rect = section.getBoundingClientRect();
        return rect.top < window.innerHeight * 0.85 && rect.bottom > 40;
      });

      const isOverInlineCTA = Array.from(ctaBlocks).some(section => {
        const rect = section.getBoundingClientRect();
        return rect.top < window.innerHeight * 0.8 && rect.bottom > 80;
      });

      if (!heroStillDominant && !isOverCriticalSection && !isOverInlineCTA) {
        setShowFloatingCTA(true);
      } else {
        setShowFloatingCTA(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    if (isModalOpen || isMobileMenuOpen) {
      html.style.overflow = 'hidden';
      html.style.height = '100%';
      body.style.overflow = 'hidden';
      body.style.height = '100%';
    } else {
      html.style.overflow = '';
      html.style.height = '';
      body.style.overflow = '';
      body.style.height = '';
    }

    return () => {
      html.style.overflow = '';
      html.style.height = '';
      body.style.overflow = '';
      body.style.height = '';
    };
  }, [isModalOpen, isMobileMenuOpen]);

  useAutoHorizontalScroll(galleryRef, { speed: 0.4 });
  useAutoHorizontalScroll(testimonialsRef, { speed: 0.35 });

  useEffect(() => {
    const targets: { key: VideoKey; ref: RefObject<HTMLDivElement> }[] = [
      { key: 'heroMobile', ref: heroRef },
      { key: 'corporate', ref: corporateRef },
      { key: 'wedding', ref: weddingRef },
    ];

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const target = targets.find(t => t.ref.current === entry.target);
            if (target) {
              handleVideoPlay(target.key);
              observer.unobserve(entry.target);
            }
          }
        });
      },
      {
        threshold: 0.6,
        rootMargin: '0px 0px -10% 0px',
      },
    );

    targets.forEach(({ ref }) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const MobileHeroBooking = () => (
    <div className="space-y-3">
      <div className="w-full bg-white border border-gray-100 rounded-2xl shadow-2xl shadow-gray-100/50 p-2 flex flex-col gap-2">
        <div className="flex flex-col gap-2">
          <div className="bg-apple-alabaster rounded-xl px-2 py-1.5 flex flex-col justify-center border border-transparent focus-within:border-champagne group transition-colors">
            <label className="text-[9px] font-bold uppercase tracking-[0.18em] text-gray-500 mb-0.5 leading-tight text-center">Event Date</label>
            <input
              type="date"
              className="bg-transparent text-[13px] font-medium text-apple-black w-full outline-none h-7 text-center"
              value={quickDate}
              onChange={(e) => setQuickDate(e.target.value)}
            />
          </div>
        </div>
        <button
          onClick={() => { openModal(undefined, quickDate || undefined); setIsMobileMenuOpen(false); }}
          className="bg-champagne hover:bg-champagne/90 text-white text-[11px] font-bold uppercase tracking-[0.2em] py-2.5 rounded-full transition-all active:scale-95 shadow-lg shadow-champagne/20 flex items-center justify-center gap-2"
        >
          <Calendar className="w-3.5 h-3.5" />
          Check Availability
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white text-apple-black font-sans overflow-x-hidden selection:bg-champagne selection:text-white">
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 30s linear infinite;
        }
        .pause-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>

      {/* 0. Global Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 transition-all duration-300 supports-[backdrop-filter]:bg-white/80">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex justify-between items-center h-16 lg:h-20">
            <img src="/black_logo.webp" alt="360BoothTime" className="h-12 lg:h-16 w-auto object-contain" loading="lazy" />

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-8 xl:gap-10">
              <button onClick={() => scrollToSection('corporate')} className="text-[10px] font-bold text-apple-black hover:text-champagne transition-colors tracking-[0.2em] uppercase py-2">Corporate</button>
              <button onClick={() => scrollToSection('weddings')} className="text-[10px] font-bold text-apple-black hover:text-champagne transition-colors tracking-[0.2em] uppercase py-2">Weddings</button>
              <button onClick={() => scrollToSection('experience')} className="text-[10px] font-bold text-apple-black hover:text-champagne transition-colors tracking-[0.2em] uppercase py-2">Experience</button>
              <button onClick={() => scrollToSection('bundles')} className="text-[10px] font-bold text-apple-black hover:text-champagne transition-colors tracking-[0.2em] uppercase py-2">Bundles</button>
              <button onClick={() => scrollToSection('gallery')} className="text-[10px] font-bold text-apple-black hover:text-champagne transition-colors tracking-[0.2em] uppercase py-2">Gallery</button>
              <button
                onClick={() => openModal(undefined, quickDate || undefined)}
                className="bg-champagne hover:bg-champagne/90 text-white px-8 py-3.5 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] transition-all active:scale-95 shadow-lg shadow-champagne/20"
              >
                Inquire
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 -mr-2 text-apple-black active:scale-90 transition-transform touch-manipulation"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-[64px] bg-white/95 backdrop-blur-xl z-40 animate-in fade-in slide-in-from-top-4 duration-500 flex flex-col border-t border-gray-100">
            <div className="px-8 py-10 space-y-1 flex-1 overflow-y-auto custom-scrollbar">
              {[
                { id: 'corporate', label: 'Corporate' },
                { id: 'weddings', label: 'Weddings' },
                { id: 'experience', label: 'Experience' },
                { id: 'bundles', label: 'Bundles' },
                { id: 'gallery', label: 'Gallery' },
              ].map(({ id, label }, idx) => (
                <button
                  key={id}
                  onClick={() => scrollToSection(id)}
                  className="block w-full text-left text-4xl font-display font-medium text-apple-black active:text-champagne py-4 transition-all animate-in slide-in-from-left-4 fade-in"
                  style={{ animationDelay: `${idx * 50}ms` }}
                >
                  {label}
                </button>
              ))}
              
              <div className="pt-10 space-y-8 animate-in slide-in-from-bottom-4 fade-in duration-700 delay-300">
                <div className="space-y-4">
                  <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gray-400">Quick Actions</p>
                  <div className="grid grid-cols-1 gap-3">
                    <a
                      href={CALL_LINK}
                      className="text-center text-[12px] font-bold uppercase tracking-[0.2em] bg-apple-black text-white py-4.5 rounded-2xl shadow-xl active:scale-95 transition-all flex items-center justify-center gap-3"
                    >
                      <Phone className="w-4 h-4 text-champagne" />
                      Call Us Now
                    </a>
                    <button
                      onClick={() => { openModal(undefined, quickDate || undefined); setIsMobileMenuOpen(false); }}
                      className="text-center text-[12px] font-bold uppercase tracking-[0.2em] bg-champagne text-white py-4.5 rounded-2xl shadow-lg active:scale-95 transition-all flex items-center justify-center gap-3"
                    >
                      <Calendar className="w-4 h-4" />
                      Check Availability
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* 1. Hero Section */}
      <section
        id="hero"
        ref={heroRef}
        className={`${lightSectionClasses} pt-8 md:pt-12 lg:pt-28 pb-8 md:pb-16 lg:pb-20 px-0 sm:px-6 mt-16`}
      >
        <div className={sectionWrapper}>
          <div className={`${cardShellClasses} rounded-none border-none sm:rounded-[32px] sm:border`}>
            <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,480px)] gap-0 items-stretch relative z-0">
              <div className="order-2 lg:order-1 text-center lg:text-left flex flex-col justify-center px-2 lg:px-12 py-4 lg:py-12 space-y-4">
                <div className="lg:hidden space-y-4 animate-in fade-in slide-in-from-bottom-8 duration-700">
                  <p className="text-sm text-apple-gray font-display font-medium leading-relaxed text-center px-2">
                    Melbourne's premier 360 video booth and luxury photo booth hire. Cinematic slow-motion content that captivates audiences and elevates every occasion.
                  </p>
      
                  <div className="text-center">
                    <MobileHeroBooking />
                  </div>
                  <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full border border-gray-100 shadow-sm">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={`hero-mobile-star-${i}`} className="w-3 h-3 fill-[#FBBC05] text-[#FBBC05]" />
                    ))}
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-apple-black">5.0</span>
                    <span className="text-xs uppercase tracking-[0.25em] text-apple-gray">Trusted at 500+ events</span>
                  </div>
                </div>
                <div className="hidden lg:block space-y-8">
                  <h1 className="text-5xl lg:text-[5.5rem] font-display font-medium tracking-tight leading-[1.1] lg:leading-[1] animate-in fade-in slide-in-from-bottom-8 duration-700">
                    Every Angle. <br />
                    <span className="text-champagne">Every Moment.</span>
                  </h1>
                  <p className="text-base lg:text-xl text-apple-gray font-display font-medium leading-relaxed max-w-2xl animate-in fade-in slide-in-from-bottom-8 duration-700 delay-100">
                    Melbourne's premier 360 video booth and luxury photo booth hire. Cinematic slow-motion content that captivates audiences and elevates every occasion.
                  </p>
                  <div className="w-full max-w-[400px] bg-white border border-gray-100 p-2 flex gap-2 mb-10 shadow-2xl shadow-gray-100/50 rounded-2xl">
                    <div className="flex-1 bg-apple-alabaster rounded-xl px-4 py-3 flex flex-col justify-center relative border border-transparent focus-within:border-champagne group transition-all">
                      <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-1">Event Date</label>
                      <input type="date" className="bg-transparent text-sm font-medium text-apple-black w-full outline-none h-6" value={quickDate} onChange={(e) => setQuickDate(e.target.value)} />
                    </div>
                    <button onClick={() => openModal(undefined, quickDate || undefined)} className="bg-champagne hover:bg-amber-600 text-white rounded-xl px-8 py-3.5 font-bold uppercase tracking-[0.2em] text-[10px] shadow-lg shadow-champagne/20 active:scale-95 transition-all">Check</button>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full border border-gray-100 shadow-sm">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star key={`hero-desktop-star-${i}`} className="w-3 h-3 fill-[#FBBC05] text-[#FBBC05]" />
                      ))}
                      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-apple-black">5.0</span>
                      <span className="text-xs uppercase tracking-[0.25em] text-apple-gray">Trusted at 500+ events</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2 relative w-full lg:h-full space-y-4 z-0">
                <div className="lg:hidden bg-white overflow-hidden shadow-xl shadow-black/5 w-full rounded-[32px]" data-cta-block="true">
                  <div className={`${featureMediaClasses} relative`}>
                    <video
                      src="/corporate_hero_high_quality.mp4"
                      className="absolute inset-0 w-full h-full object-cover"
                      style={heroVideoStyle}
                      autoPlay
                      playsInline
                      muted
                      loop
                      preload="none"
                      onLoadedData={() => handleVideoLoaded('heroMobile')}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex flex-col justify-end p-6 text-left pb-8 items-start pointer-events-none">
                      <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-gray-300 mb-2">Melbourne Elite 360</div>
                      <p className="text-3xl font-display font-medium text-white leading-tight tracking-tight">
                        Every Angle. <br />
                        <span className="text-champagne">Every Moment.</span>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="hidden lg:block bg-white overflow-hidden shadow-2xl shadow-black/5 w-full h-full rounded-[32px]" data-cta-block="true">
                  <div className={`${featureMediaClasses} relative h-full`}>
                    <video
                      src="/corporate_hero_high_quality.mp4"
                      className="absolute inset-0 w-full h-full object-cover"
                      style={heroVideoStyle}
                      autoPlay
                      playsInline
                      muted
                      loop
                      preload="none"
                      onLoadedData={() => handleVideoLoaded('heroMobile')}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex flex-col justify-end p-6 text-left pb-8 items-start pointer-events-none">
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Corporate Banner */}
      <section id="corporate" ref={corporateRef} className={`${lightSectionClasses} pt-12 md:pt-16 lg:pt-20 pb-12 md:pb-16 lg:pb-20 px-0 sm:px-6`}>
        <div className={sectionWrapper}>
          <div className={cardShellClasses} data-cta-block="true">
            <div className="grid lg:grid-cols-[minmax(0,480px)_minmax(0,1fr)] gap-0 items-stretch">
              <div className="relative w-full">
                <div className="md:hidden bg-white overflow-hidden w-full h-full" data-cta-block="true">
                  <div className={`${featureMediaClasses} relative`}>
                    <img
                      src="/images/optimized/corporate_poster.webp"
                      alt="Corporate 360 booth highlight"
                      className={`absolute inset-0 w-full h-full object-cover object-[center_30%] transition-opacity duration-500 ${
                        videoLoaded.corporate ? 'opacity-0' : 'opacity-100'
                      }`}
                      loading="lazy"
                    />
                    <div
                      style={heroOverlayStyle}
                      className={`transition-opacity duration-500 ${
                        videoLoaded.corporate ? 'opacity-0' : 'opacity-20'
                      }`}
                    />
                    {videoPlayback.corporate && (
                      <video
                        src="/corporatevideo_ultra2.mp4"
                        className={`absolute inset-0 transition-opacity duration-700 ${
                          videoLoaded.corporate ? 'opacity-100' : 'opacity-0'
                        }`}
                        style={heroVideoStyle}
                        autoPlay
                        playsInline
                        muted
                        loop
                        preload="none"
                        onLoadedData={() => handleVideoLoaded('corporate')}
                      />
                    )}
                    {!videoLoaded.corporate && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/10 backdrop-blur-sm">
                        <div className="w-8 h-8 border-2 border-champagne border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex flex-col justify-end p-6 text-left pb-8 items-start pointer-events-none">
                      <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-gray-300 mb-2">For Business</div>
                      <h2 className="text-3xl font-display font-medium text-white leading-tight tracking-tight">
                        Content that Drives <span className="text-champagne">ROI.</span>
                      </h2>
                    </div>
                  </div>
                </div>
                <div className="hidden md:block bg-white overflow-hidden w-full h-full rounded-[32px]" data-cta-block="true">
                  <div className={`${featureMediaClasses} relative h-full`}>
                    <img
                      src="/images/optimized/corporate_poster.webp"
                      alt="Corporate 360 booth highlight desktop"
                      className={`absolute inset-0 w-full h-full object-cover object-[center_30%] transition-opacity duration-500 ${
                        videoLoaded.corporate ? 'opacity-0' : 'opacity-100'
                      }`}
                      loading="lazy"
                    />
                    <div
                      style={heroOverlayStyle}
                      className={`transition-opacity duration-500 ${
                        videoLoaded.corporate ? 'opacity-0' : 'opacity-20'
                      }`}
                    />
                    {videoPlayback.corporate && (
                      <video
                        src="/corporatevideo_ultra2.mp4"
                        className={`absolute inset-0 transition-opacity duration-700 ${
                          videoLoaded.corporate ? 'opacity-100' : 'opacity-0'
                        }`}
                        style={heroVideoStyle}
                        autoPlay
                        playsInline
                        muted
                        loop
                        preload="none"
                        onLoadedData={() => handleVideoLoaded('corporate')}
                      />
                    )}
                    {!videoLoaded.corporate && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/10 backdrop-blur-sm">
                        <div className="w-8 h-8 border-2 border-champagne border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex flex-col justify-end p-6 text-left pb-8 items-start pointer-events-none">
                      <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-gray-300 mb-2">For Business</div>
                      <h2 className="text-3xl font-display font-medium text-white leading-tight tracking-tight">
                        Content that Drives <span className="text-champagne">ROI.</span>
                      </h2>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 lg:px-12 py-8 lg:py-12 text-left">
                <div className={`hidden md:block ${pillLabelClasses} mb-6`}>For Business</div>
                <h2 className="hidden md:block text-6xl font-display font-medium mb-8 text-apple-black leading-tight tracking-tight">
                  Content that Drives <span className="text-champagne">ROI.</span>
                </h2>
                <div className="text-gray-700 text-base md:text-lg font-display font-medium leading-relaxed max-w-2xl mx-0 space-y-4 mb-6">
                  <p>Transform your corporate event into shareable branded content.</p>
                  <p>
                    Our 360° video booth delivers assets built for LinkedIn and TikTok. <span className="text-apple-black/80">Stay present in your audience’s feeds long after guests leave.</span>
                  </p>
                  <p>
                    With custom branding, instant sharing, and lead capture, every interaction becomes a measurable marketing touchpoint.
                  </p>
                </div>
                <div className="text-gray-700 text-base md:text-lg font-display font-medium leading-relaxed max-w-2xl mx-0 mb-12">
                  <p>Perfect for product launches, brand activations, corporate galas, and trade shows.</p>
                </div>
                <div className="flex justify-center pt-2">
                  <button onClick={() => openModal('Corporate')} className={`${primaryCtaClasses} w-full max-w-sm`}>
                    Request Proposal
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Wedding Banner */}
      <section id="weddings" ref={weddingRef} className={`${lightSectionClasses} pt-12 md:pt-16 lg:pt-20 px-0 sm:px-6`}>
        <div className={sectionWrapper}>
          <div className={cardShellClasses} data-cta-block="true">
            <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,480px)] gap-0 items-stretch">
              <div className="order-1 lg:order-2 relative w-full lg:h-full space-y-4 z-0">
                <div className="lg:hidden bg-white overflow-hidden shadow-xl shadow-black/5 w-full rounded-[32px]" data-cta-block="true">
                  <div className={`${featureMediaClasses} relative`}>
                    <img
                      src="/images/optimized/wedding_poster.webp"
                      alt="Wedding highlight still"
                      className={`absolute inset-0 w-full h-full object-cover object-[center_30%] transition-opacity duration-500 ${
                        videoLoaded.wedding ? 'opacity-0' : 'opacity-100'
                      }`}
                      loading="lazy"
                    />
                    <div
                      style={heroOverlayStyle}
                      className={`transition-opacity duration-500 ${
                        videoLoaded.wedding ? 'opacity-0' : 'opacity-20'
                      }`}
                    />
                    {videoPlayback.wedding && (
                      <video
                        src="/Untitled_design_ultra2.mp4"
                        className={`absolute inset-0 transition-opacity duration-700 ${
                          videoLoaded.wedding ? 'opacity-100' : 'opacity-0'
                        }`}
                        style={heroVideoStyle}
                        autoPlay
                        playsInline
                        muted
                        loop
                        preload="none"
                        onLoadedData={() => handleVideoLoaded('wedding', { delayMs: 150 })}
                      />
                    )}
                    {!videoLoaded.wedding && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/10 backdrop-blur-sm">
                        <div className="w-8 h-8 border-2 border-champagne border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex flex-col justify-end p-6 text-left pb-8 items-start pointer-events-none">
                      <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-gray-300 mb-2">For Romance</div>
                      <h1 className="text-3xl font-display font-medium text-white leading-tight tracking-tight">
                        Cinematic <br />
                        <span className="text-champagne">Elegance.</span>
                      </h1>
                    </div>
                  </div>
                </div>
                <div className="hidden md:block bg-white overflow-hidden w-full h-full rounded-[32px]" data-cta-block="true">
                  <div className={`${featureMediaClasses} relative h-full`}>
                    <img
                      src="/images/optimized/wedding_poster.webp"
                      alt="Wedding 360 booth highlight desktop"
                      className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-500 ${
                        videoLoaded.wedding ? 'opacity-0' : 'opacity-100'
                      }`}
                      loading="lazy"
                    />
                    <div
                      style={heroOverlayStyle}
                      className={`transition-opacity duration-500 ${
                        videoLoaded.wedding ? 'opacity-0' : 'opacity-20'
                      }`}
                    />
                    {videoPlayback.wedding && (
                      <video
                        src="/Untitled_design_ultra2.mp4"
                        className={`absolute inset-0 transition-opacity duration-700 ${
                          videoLoaded.wedding ? 'opacity-100' : 'opacity-0'
                        }`}
                        style={heroVideoStyle}
                        autoPlay
                        playsInline
                        muted
                        loop
                        preload="none"
                        onLoadedData={() => handleVideoLoaded('wedding', { delayMs: 150 })}
                      />
                    )}
                    {!videoLoaded.wedding && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/10 backdrop-blur-sm">
                        <div className="w-8 h-8 border-2 border-champagne border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex flex-col justify-end p-6 text-left pb-8 items-start pointer-events-none">
                    </div>
                  </div>
                </div>
              </div>

              <div className="order-2 lg:order-1 px-6 lg:px-12 py-8 lg:py-12 text-left">
                <div className={`hidden md:block ${pillLabelClasses} mb-6`}>For Romance</div>
                <h2 className="hidden md:block text-6xl font-display font-medium mb-8 text-apple-black leading-tight tracking-tight">
                  Cinematic <span className="text-champagne">Elegance.</span>
                </h2>
                <div className="text-gray-700 text-base md:text-lg font-display font-medium leading-relaxed max-w-2xl mx-0 space-y-4 mb-10">
                  <p>Cinematic slow-motion moments with lighting designed to flatter.</p>
                  <p>
                    Elegant footage that captures flowing dresses and makes every guest look incredible. The kind of content people actually want
                    to share, not just save and forget.
                  </p>
                </div>
                <div className="flex justify-center pt-2">
                  <button onClick={() => openModal('Wedding')} className={`${primaryCtaClasses} w-full max-w-sm`}>
                    Secure Your Date
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. The Experience */}
      <section id="experience" className={lightSectionClasses}>
        <div className={`${sectionWrapper} ${sectionStackClasses}`}>
          <div className={`text-center ${sectionIntroClasses}`}>
            <div className={accentBarClasses}></div>
            <h2 className={sectionTitleClasses}>The <span className="text-champagne">Experience</span></h2>
            <p className={`${sectionSubtitleClasses} max-w-2xl mx-auto`}>World-class hardware powered by a seamless workflow.</p>
          </div>

          {/* Product Cards */}
          <div className="grid md:grid-cols-2 gap-6 lg:gap-10 items-stretch" data-cta-block="true">
            {/* 360 Booth Card */}
            <div className={`${cardShellClasses} group relative transition-all duration-500 hover:shadow-2xl flex flex-col`}>
              <div className="aspect-[4/3] overflow-hidden relative">
                <img
                  src="/images/optimized/IMG_6654.jpg"
                  alt="360 Video Booth"
                  className="w-full h-full object-cover object-[center_40%] group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
              </div>
              <div className="p-6 md:p-8 text-left flex flex-col flex-1">
                <div className="space-y-4">
                  <h3 className="text-xl md:text-2xl font-display font-medium">The 360° Booth</h3>
                  <p className="text-apple-gray font-display font-medium text-sm md:text-base leading-relaxed">
                    High-speed robotic arm captures cinematic slow-motion in stunning clarity. Professional attendant included.
                  </p>
                  <ul className="space-y-2">
                    {[
                      'Custom branded overlays',
                      'Glamour Mode lighting',
                      'Instant social sharing',
                      'Branded digital gallery',
                      'Unlimited sessions'
                    ].map((item) => (
                      <li key={item} className="flex items-center gap-3 text-apple-gray text-sm md:text-base font-display font-medium">
                        <Check className="w-4 h-4 text-champagne flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-auto pt-4">
                  <button onClick={() => openModal(undefined, quickDate || undefined)} className={`${primaryCtaClasses} w-full`}>
                    Request 360
                  </button>
                </div>
              </div>
            </div>

            {/* Photo Booth Card */}
            <div className={`${cardShellClasses} group relative transition-all duration-500 hover:shadow-2xl flex flex-col`}>
              <div className="aspect-[4/3] overflow-hidden relative">
                <img src="/images/optimized/IMG_1253_ultra.webp" alt="Studio Photo Booth" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
              </div>
              <div className="p-6 md:p-8 text-left flex flex-col flex-1">
                <div className="space-y-4">
                  <h3 className="text-xl md:text-2xl font-display font-medium">The Photo Booth</h3>
                  <p className="text-apple-gray font-display font-medium text-sm md:text-base leading-relaxed">
                    Instant prints with flattering Glamour Mode lighting. Professional attendant included.
                  </p>
                  <ul className="space-y-2">
                    {[
                      'Instant high-quality prints',
                      'Glamour Mode lighting',
                      'Digital + physical keepsakes',
                      'Custom branded templates',
                      'Unlimited sessions'
                    ].map((item) => (
                      <li key={item} className="flex items-center gap-3 text-apple-gray text-sm md:text-base font-display font-medium">
                        <Check className="w-4 h-4 text-champagne flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-auto pt-4">
                  <button onClick={() => openModal('Other')} className={`${primaryCtaClasses} w-full`}>
                    Request Booth
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* 6. The Bundle */}
      <section id="bundles" className={`${darkSectionClasses} border-y border-gray-100`}>
        {/* Subtle background accents */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(250,214,165,0.25),transparent_60%)] pointer-events-none"></div>

        <div className={`${sectionWrapper} max-w-[1100px] relative z-10 text-center`}>
          <div className="inline-block bg-champagne/10 text-champagne text-[9px] font-bold uppercase tracking-[0.2em] px-4 py-2 rounded-full mb-6 border border-champagne/30">
            Limited Offer
          </div>
          <h2 className="text-4xl md:text-6xl font-display font-medium mb-6 md:mb-8 tracking-tight text-apple-black">
            The Full <span className="text-champagne">Spectrum.</span>
          </h2>
          <p className="text-base md:text-xl text-apple-gray max-w-2xl mx-auto mb-10 font-light leading-relaxed">
            Combine the 360 Video Booth with our Still Photo Booth for the ultimate event coverage.
          </p>

          <div className="relative max-w-3xl mx-auto mb-10 overflow-hidden bg-white border border-gray-100 rounded-[36px] shadow-2xl shadow-gray-200/70">
            <div className="grid grid-cols-2 sm:flex sm:flex-row items-center justify-center p-6 sm:p-8 lg:p-12 gap-6 sm:gap-16 lg:gap-20 relative">
              {/* 360 Video */}
              <div className="text-center">
                <div className="text-2xl sm:text-3xl lg:text-4xl font-display mb-1 text-apple-black">360°</div>
                <div className="text-[9px] sm:text-[10px] text-gray-500 uppercase tracking-[0.2em] font-bold">Video</div>
              </div>

              {/* PLUS SIGN - Absolute on mobile, static on desktop */}
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 sm:static sm:translate-x-0 sm:translate-y-0 text-xl sm:text-2xl font-display text-champagne z-10 bg-champagne/10 rounded-full w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center border border-champagne/30 sm:border-0 sm:bg-transparent">
                +
              </div>

              {/* Photo Stills */}
              <div className="text-center">
                <div className="text-2xl sm:text-3xl lg:text-4xl font-display mb-1 text-apple-black">Booth</div>
                <div className="text-[9px] sm:text-[10px] text-gray-500 uppercase tracking-[0.2em] font-bold">Stills</div>
              </div>

              {/* Desktop Divider */}
              <div className="hidden sm:block w-px h-12 lg:h-16 bg-gray-100"></div>

              {/* Desktop Discount Item */}
              <div className="hidden sm:block text-center">
                <div className="text-3xl lg:text-4xl font-display text-apple-black mb-1">10%</div>
                <div className="text-[10px] text-gray-400 uppercase tracking-[0.2em] font-bold">Discount</div>
              </div>
            </div>

            {/* Mobile Discount Bar */}
            <div className="sm:hidden bg-champagne/10 py-3 text-center border-t border-gray-100">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-champagne">Save 10% When Bundled</span>
            </div>
          </div>

          <div>
            <button
              onClick={() => openModal(undefined, quickDate || undefined)}
              className={`${primaryCtaClasses} px-12 shadow-2xl shadow-champagne/20`}
            >
              Get The Bundle
            </button>
          </div>
        </div>
      </section>

      {/* 7. Trusted By (Logo Cloud) */}
      <section className={`${lightSectionClasses} border-y border-gray-100`}>
        <div className={`max-w-[1600px] mx-auto px-4 sm:px-10 ${sectionStackClasses}`}>
          <div className="max-w-2xl mx-auto text-center space-y-4">
            <div className={accentBarClasses}></div>
            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-gray-500">
                Trusted by Melbourne's Best
              </p>
              <p className="text-sm md:text-base text-apple-gray/90">
                Partners that rely on us for brand-safe experiences.
              </p>
            </div>
          </div>
          <div className="relative overflow-hidden rounded-[32px] py-6 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex animate-marquee pause-marquee items-center">
              {[...trustedPartners, ...trustedPartners].map((partner, index) => (
                <div
                  key={`${partner.name}-${index}`}
                  className="flex items-center justify-center flex-shrink-0 px-4 lg:px-8"
                >
                  {partner.logo ? (
                    <img
                      src={partner.logo}
                      alt={partner.name}
                      className="h-10 sm:h-12 lg:h-14 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity duration-300"
                      loading="lazy"
                      onError={(e) => {
                        console.error('Logo failed to load:', partner.logo);
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>


      <section id="gallery" className={`${lightSectionClasses} border-t border-gray-100 overflow-hidden`}>
        <div className={`${wideSectionWrapper} ${sectionStackClasses}`}>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div className={sectionIntroClasses}>
              <p className={`${pillLabelClasses} mb-0`}>Gallery</p>
              <h2 className={sectionTitleClasses}>Captured Moments</h2>
            </div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-gray-400">Scroll to explore</div>
          </div>

          <div className={`${carouselTrackClasses} pt-2`}>
            {galleryImages.map((image, index) => (
              <div key={image} className={`${imageCarouselCardClasses} cursor-pointer`} style={carouselCardCropStyle}>
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-all z-10 backdrop-blur-[2px]">
                  <ImageIcon className="w-6 h-6 text-white opacity-90" />
                </div>
                <img
                  src={image}
                  alt={`Gallery Item ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. Client Love / Testimonials */}
      <section className={`${lightSectionClasses} border-t border-gray-100 overflow-hidden`}>
        <div className={`${wideSectionWrapper} ${sectionStackClasses}`}>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div className={sectionIntroClasses}>
              <p className={`${pillLabelClasses} mb-0`}>Client Love</p>
              <h2 className={sectionTitleClasses}>Testimonials</h2>
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-gray-400">Trusted by 250+ clients</div>
          </div>

          <div ref={testimonialsRef} className={`${carouselTrackClasses} pt-2`}>
            {premiumTestimonials.map((review, index) => (
              <article key={`${review.reviewer}-${index}`} className={`${contentCarouselCardClasses} min-h-[360px] hover:shadow-2xl transition-all duration-300 relative group/card`} style={carouselCardCropStyle}>
                <div className="flex gap-0.5 mb-6">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={`${review.reviewer}-${star}`} className="w-3.5 h-3.5 fill-[#FBBC05] text-[#FBBC05]" />
                  ))}
                </div>
                <p className="font-sans text-sm lg:text-base leading-relaxed text-apple-black mb-6 italic">
                  &ldquo;{review.comment}&rdquo;
                </p>
                <div className="flex items-center gap-4 mt-auto pt-6 border-t border-gray-50">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-[12px] font-display font-semibold text-apple-black">
                    {getReviewerInitials(review.reviewer)}
                  </div>
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-widest text-apple-black flex items-center gap-2">
                      {review.reviewer}
                      {review.segment && (
                        <span className="text-[9px] font-medium tracking-[0.3em] uppercase text-gray-400">
                          {review.segment}
                        </span>
                      )}
                      <div className="w-2.5 h-2.5 bg-blue-500 rounded-full flex items-center justify center">
                        <Check className="w-2 h-2 text-white" strokeWidth={4} />
                      </div>
                    </div>
                    <div className="text-[9px] text-gray-400 uppercase tracking-widest mt-0.5">
                      {getTestimonialTime(review.relativeTimeDescription, review.createTime)}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className={`${lightSectionClasses} border-t border-gray-100`}>
        <div className={`${wideSectionWrapper}`}>
          <div className="rounded-[48px] border border-white/60 bg-gradient-to-br from-white via-apple-alabaster/70 to-white px-6 py-12 sm:px-12 sm:py-16 shadow-[0_45px_120px_rgba(15,23,42,0.08)] space-y-10 text-center lg:text-left">
            <div className="space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-champagne">Inquiry</p>
              <h2 className={`${sectionTitleClasses} text-4xl md:text-[56px] leading-tight`}>
                Ready to make it <span className="text-champagne italic">unforgettable?</span>
              </h2>
              <p className="text-base md:text-lg text-apple-gray/90 max-w-2xl whitespace-normal lg:whitespace-nowrap">
                Drop your details inside this portal to check availability. We confirm within hours and walk you through the final steps.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-4 md:gap-6 items-stretch">
              {[
                {
                  label: 'Call us',
                  value: PHONE_NUMBER_DISPLAY,
                  detail: 'SMS & WhatsApp friendly',
                  icon: Phone,
                  cta: true,
                  action: CALL_LINK,
                },
                {
                  label: 'Email',
                  value: 'info@360boothtime.com.au',
                  detail: 'Replies within 2 hours',
                  icon: Mail,
                  cta: true,
                  action: 'mailto:info@360boothtime.com.au',
                },
                {
                  label: 'Check availability',
                  value: 'Launch portal',
                  detail: 'Takes under 2 minutes',
                  icon: Calendar,
                  cta: true,
                },
              ].map(({ label, value, detail, icon: Icon, cta, action }) => (
                <div
                  key={label}
                  className="rounded-2xl md:rounded-3xl border border-white/70 bg-white p-5 md:p-6 shadow-[0_18px_45px_rgba(15,23,42,0.08)] flex flex-col h-full"
                >
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="w-10 h-10 md:w-11 md:h-11 rounded-xl md:rounded-2xl bg-champagne/15 text-champagne flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="text-left flex-1">
                      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">{label}</p>
                      <p className="text-sm md:text-base font-display font-medium text-apple-black">{value}</p>
                    </div>
                  </div>
                  <p className="text-xs md:text-sm text-apple-gray mt-3 flex-1">{detail}</p>
                  {cta && (
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      <button
                        onClick={() => {
                          if (action) {
                            if (action.startsWith('mailto:')) {
                              window.location.href = action;
                            } else if (action.startsWith('tel:')) {
                              window.location.href = action;
                            }
                          } else {
                            openModal(undefined, quickDate || undefined);
                          }
                        }}
                        className="w-full bg-champagne text-white text-[11px] font-bold uppercase tracking-[0.3em] px-4 md:px-6 py-2.5 rounded-full hover:bg-champagne/90 transition-colors shadow-lg shadow-champagne/20 flex items-center justify-center gap-2 active:scale-95"
                      >
                        {action ? (action.startsWith('mailto:') ? 'Send Email' : action.startsWith('tel:') ? 'Call Now' : 'Enter Portal') : 'Enter Portal'}
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 10. Footer */}
      <footer className="bg-white pt-8 pb-6 border-t border-gray-100 px-4 sm:px-6">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-4 gap-8 md:gap-12 mb-8">
          <div className="col-span-1 md:col-span-2">
            <h3 className="font-display font-medium text-xl md:text-2xl mb-6 tracking-tight text-apple-black">360BoothTime</h3>
            <p className="text-apple-gray max-w-sm mb-8 text-[10px] md:text-[11px] font-light leading-relaxed uppercase tracking-[0.1em]">
              Melbourne's premier event technology provider. Bridging the gap between professional production and social entertainment.
            </p>
            <div className="flex gap-3 md:gap-4">
              <a
                href="https://www.instagram.com/360boothtime/"
                target="_blank"
                rel="noreferrer"
                className="text-apple-black hover:text-champagne transition-colors uppercase text-[10px] font-bold tracking-widest"
              >
                Instagram
              </a>
              <a
                href="https://www.tiktok.com/@360boothtimeau?_t=8cMJYmWTGJF&_r=1&fbclid=PAZXh0bgNhZW0CMTEAc3J0YwZhcHBfaWQMMjU2MjgxMDQwNTU4AAGnySBSjUh77MTSV-Mj1_LinmniIxDkJZWCQbKtPOSwElWmK_koydAa38Kan7g_aem_cWDyqQCAsOw93a6vbFfONg"
                target="_blank"
                rel="noreferrer"
                className="text-apple-black hover:text-champagne transition-colors uppercase text-[10px] font-bold tracking-widest"
              >
                TikTok
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-bold text-apple-black mb-6 uppercase tracking-[0.2em] text-[10px]">Experiences</h4>
            <ul className="space-y-3 md:space-y-4 text-apple-gray text-xs font-medium uppercase tracking-widest">
              <li><button onClick={() => scrollToSection('corporate')} className="hover:text-apple-black transition-colors">Corporate</button></li>
              <li><button onClick={() => scrollToSection('weddings')} className="hover:text-apple-black transition-colors">Weddings</button></li>
              <li><button onClick={() => scrollToSection('experience')} className="hover:text-apple-black transition-colors">Photo Booth</button></li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-apple-black mb-6 uppercase tracking-[0.2em] text-[10px]">Legal</h4>
            <ul className="space-y-3 md:space-y-4 text-apple-gray text-xs font-medium uppercase tracking-widest">
              <li>Privacy Policy</li>
              <li>Terms of Service</li>
            </ul>
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto pt-6 md:pt-8 border-t border-gray-100 flex flex-col md:flex-row justify-between items-center gap-3 md:gap-4 text-apple-gray text-[9px] uppercase tracking-[0.2em]">
          <div>&copy; {new Date().getFullYear()} 360 Booth Time.</div>
          <div>Melbourne, Australia</div>
        </div>
      </footer>

      {/* Global Components */}
      <FloatingCTA isVisible={showFloatingCTA} />
      <Suspense fallback={null}>
        <ChakraBookingModal
          isOpen={isModalOpen}
          onClose={closeModal}
          initialEventType={selectedEventType}
          initialDate={selectedDate}
        />
      </Suspense>
    </div>
  );
}

export default App;